import { useState, type ReactNode } from 'react';
import { useSettingsStore, type AppSettings } from '../../stores/settingsStore';
import { toFriendlyErrorMessage } from '../../utils/friendlyErrors';

const INPUT_CLASSES =
  'w-full bg-card border border-input rounded-lg text-foreground text-sm px-3 py-2 focus:border-ring focus:ring-1 focus:ring-ring outline-none';

const TOGGLE_BASE_CLASSES = 'rounded-lg border border-input px-3 py-2 text-sm transition-colors';

const TEXTAREA_CLASSES =
  'w-full h-80 bg-card border border-input rounded-lg text-foreground text-sm px-4 py-3 font-mono focus:border-ring outline-none resize-y';

export default function GeneralTab() {
  const settings = useSettingsStore((s) => s.settings);
  const updateGeneral = useSettingsStore((s) => s.updateGeneral);
  const updateSettings = useSettingsStore((s) => s.updateSettings);
  const resetSettings = useSettingsStore((s) => s.resetSettings);

  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const { displayName, defaultGatewayUrl, autoReconnect, maxReconnectRetries } = settings.general;

  const openJsonEditor = () => {
    setJsonText(JSON.stringify(settings, null, 2));
    setJsonError(null);
    setJsonEditorOpen(true);
  };

  const toggleJsonEditor = () => {
    if (jsonEditorOpen) {
      setJsonEditorOpen(false);
      setJsonError(null);
      return;
    }

    openJsonEditor();
  };

  const applyJson = () => {
    try {
      const parsed: unknown = JSON.parse(jsonText);

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Settings JSON must be an object');
      }

      updateSettings(parsed as Partial<AppSettings>);
      setJsonError(null);
      setJsonEditorOpen(false);
    } catch (error) {
      console.error('[GeneralTab] Failed to apply settings JSON:', error);
      setJsonError(toFriendlyErrorMessage(error, 'settings-json'));
    }
  };

  const handleResetDefaults = () => {
    const shouldReset = confirm('Reset all settings to defaults? This cannot be undone.');
    if (!shouldReset) {
      return;
    }

    resetSettings();
    setJsonError(null);

    if (jsonEditorOpen) {
      const nextSettings = useSettingsStore.getState().settings;
      setJsonText(JSON.stringify(nextSettings, null, 2));
    }
  };

  return (
    <div className="space-y-6">
      <SettingSection
        title="Display Name"
        description="Used for Yuki private sessions and Family Chat message prefixes."
      >
        <input
          type="text"
          value={displayName}
          onChange={(event) => updateGeneral({ displayName: event.target.value })}
          className={INPUT_CLASSES}
          placeholder="e.g. Feifei"
        />
      </SettingSection>

      <div className="border-t border-border pt-6">
        <SettingSection title="Default Gateway URL" description="Pre-fills the connect screen URL.">
          <input
            type="text"
            value={defaultGatewayUrl}
            onChange={(event) => updateGeneral({ defaultGatewayUrl: event.target.value })}
            className={INPUT_CLASSES}
            placeholder="wss://clawlet.robbieai.com/gateway/"
          />
        </SettingSection>
      </div>

      <div className="border-t border-border pt-6">
        <SettingSection
          title="Auto-Reconnect"
          description="Automatically reconnect when the connection drops."
        >
          <div className="space-y-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => updateGeneral({ autoReconnect: true })}
                className={`${TOGGLE_BASE_CLASSES} ${
                  autoReconnect
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-muted'
                }`}
              >
                On
              </button>
              <button
                type="button"
                onClick={() => updateGeneral({ autoReconnect: false })}
                className={`${TOGGLE_BASE_CLASSES} ${
                  !autoReconnect
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-muted'
                }`}
              >
                Off
              </button>
            </div>

            {autoReconnect && (
              <div className="max-w-44">
                <label htmlFor="max-reconnect-retries" className="mb-1 block text-xs text-muted-foreground">
                  Max retries (1-50)
                </label>
                <input
                  id="max-reconnect-retries"
                  type="number"
                  min={1}
                  max={50}
                  value={maxReconnectRetries}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (Number.isNaN(next)) {
                      return;
                    }

                    const clamped = Math.min(50, Math.max(1, Math.trunc(next)));
                    updateGeneral({ maxReconnectRetries: clamped });
                  }}
                  className={INPUT_CLASSES}
                />
              </div>
            )}
          </div>
        </SettingSection>
      </div>

      <div className="border-t border-border pt-6">
        <SettingSection
          title="Advanced"
          description="Power-user controls for raw settings editing and full reset."
        >
          <div className="space-y-3">
            <button
              type="button"
              onClick={toggleJsonEditor}
              className={`${TOGGLE_BASE_CLASSES} ${
                jsonEditorOpen
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-muted'
              }`}
            >
              {jsonEditorOpen ? 'Close Settings (JSON)' : 'Open Settings (JSON)'}
            </button>

            {jsonEditorOpen && (
              <div>
                <textarea
                  value={jsonText}
                  onChange={(event) => setJsonText(event.target.value)}
                  className={TEXTAREA_CLASSES}
                  spellCheck={false}
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={applyJson}
                    className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={handleResetDefaults}
                    className="rounded-lg border border-input bg-secondary px-3 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
                  >
                    Reset to Defaults
                  </button>
                </div>

                {jsonError && <p className="mt-1 text-xs text-destructive-foreground">{jsonError}</p>}
              </div>
            )}
          </div>
        </SettingSection>
      </div>
    </div>
  );
}

function SettingSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-0.5 text-sm font-semibold text-foreground">{title}</h4>
      <p className="mb-3 text-xs text-muted-foreground">{description}</p>
      {children}
    </div>
  );
}
